/** The base type of an entity. */
export type BaseEntity = { id: string };

/** The base type of an id path. */
export type BaseIdKeys = { [key: string]: string | null };

/** An enum of possible mutation actions. */
export type HookAction = "create" | "update" | "delete";

/** Possible values that a registered hook's `before` / `after` methods can return. */
export type HookReturn<Entity> =
  | Entity
  | void
  | undefined
  | Promise<Entity | void | undefined>;

/** A table for finding the argument type sent to `before` / `after`, based on the action. */
export type HookArgTypeByAction<Entity extends BaseEntity> = {
  create: {
    before: Partial<Entity>;
    after: Entity;
  };
  update: {
    before: Partial<Entity>;
    after: Entity;
  };
  delete: {
    before: null;
    after: null;
  };
};

/** An individual method in the method table returned from a hook handler. */
export type HookMethod<
  Entity extends BaseEntity,
  Action extends HookAction,
  Insertion extends "before" | "after",
> = (
  entity: HookArgTypeByAction<Entity>[Action][Insertion]
) => HookReturn<HookArgTypeByAction<Entity>[Action][Insertion]>;

/** The method table returned from a hook handler. None of the methods are required. */
export type HookMethods<Entity extends BaseEntity> = {
  beforeCreate?: HookMethod<Entity, "create", "before">;
  afterCreate?: HookMethod<Entity, "create", "after">;
  beforeUpdate?: HookMethod<Entity, "update", "before">;
  afterUpdate?: HookMethod<Entity, "update", "after">;
  beforeDelete?: HookMethod<Entity, "delete", "before">;
  afterDelete?: HookMethod<Entity, "delete", "after">;
};

/** A hook handler. Optionally returns hook methods `before` and `after`, which are invoked at
 * the proper times. If the handler should not handle the action, returning nothing or
 * `undefined` will leave it out. */
export type HookHandler<
  IdPath extends BaseIdKeys,
  Entity extends BaseEntity,
  Action extends HookAction,
> = (ids: IdPath, action: Action) => HookMethods<Entity> | void | undefined;

/** The type for registered hook entries. */
type HookRegistryEntry<IdPath extends BaseIdKeys, Entity extends BaseEntity> = {
  priority: number;
  handler: HookHandler<IdPath, Entity, HookAction>;
};

const capitalizeAction: Record<HookAction, Capitalize<HookAction>> = {
  create: "Create",
  update: "Update",
  delete: "Delete",
};

/** Manages registering side-actions for a repo when an entity is saved or updated. */
export class RepoMutationHooks<
  IdPath extends BaseIdKeys,
  Entity extends BaseEntity,
> {
  /** A list of the registered hooks and their priorities. */
  private hooks: HookRegistryEntry<IdPath, Entity>[] = [];
  /** If the hooks list has been sorted since last modified. */
  private sorted: boolean = true;

  constructor() {
    this.addHook = this.addHook.bind(this);
    this.removeHook = this.removeHook.bind(this);
    this.execute = this.execute.bind(this);
  }

  /** Add a new handler to the hook registry. Use `priority` to indicate when it is called among
   * other registered hooks. Hooks with lower priorities are called before hooks with higher
   * priorities. Priority defaults to 0, and negative and non-integer values are accepted.
   *
   * The handler will be called once each time an entity in the repo is created / updated /
   * deleted. It will be passed an object with the id keys of the entity, and the action being
   * triggered. It optionally returns an object with optional `before` and `after` methods that
   * will be invoked before and after the action occurs.
   *
   * The `before` and `after` methods may choose to return an updated version of the entity. If
   * `undefined` or nothing is returned, the entity will not be replaced. However, direct
   * modifications of the entity will persist. */
  addHook(
    handler: HookHandler<IdPath, Entity, HookAction>,
    priority: number = 0
  ) {
    this.hooks.push({ priority, handler });
    this.sorted = false;
  }

  /** Remove a registered handler from the hook registry. */
  removeHook(handler: HookHandler<IdPath, Entity, HookAction>) {
    const index = this.hooks.findIndex((hook) => hook.handler === handler);
    if (index > -1) this.hooks.splice(index, 1);
  }

  /** Start executing an action. Returns an object with `before` and `after` which should be
   * invoked before and after creating / updating / deleting, respectively.
   *
   * Both `before` and `after` receive the original entity (or partial entity, for `before`),
   * and return the modified entity asynchronously, after executing all registered hooks. */
  execute<Action extends HookAction>(ids: IdPath, action: Action) {
    type Before = HookArgTypeByAction<Entity>[Action]["before"];
    type After = HookArgTypeByAction<Entity>[Action]["after"];

    // sort if necessary
    if (!this.sorted) {
      this.hooks.sort((a, b) => a.priority - b.priority);
      this.sorted = true;
    }

    // build method list
    const before: HookMethod<Entity, Action, "before">[] = [];
    const after: HookMethod<Entity, Action, "after">[] = [];

    const beforeKey = ("before" +
      capitalizeAction[action]) as `before${Capitalize<Action>}`;
    const afterKey = ("after" +
      capitalizeAction[action]) as `after${Capitalize<Action>}`;

    for (const hook of this.hooks) {
      const table = hook.handler(ids, action);
      if (table) {
        if (table[beforeKey]) {
          before.push(
            table[beforeKey].bind(table) as HookMethod<Entity, Action, "before">
          );
        }
        if (table[afterKey]) {
          after.push(
            table[afterKey].bind(table) as HookMethod<Entity, Action, "after">
          );
        }
      }
    }

    // return `before` / `after`
    return {
      /** Run this before creating / updating / deleting an entity. */
      async before(entity: Before): Promise<Before> {
        for (const method of before) {
          const response = await method(entity);
          if (response) entity = response;
        }
        return entity;
      },
      /** Run this after creating / updating / deleting an entity. */
      async after(entity: After): Promise<After> {
        for (const method of after) {
          const response = await method(entity);
          if (response) entity = response;
        }
        return entity;
      },
    };
  }
}
